import { userManager } from '@/config/keycloak';
import type { ModelType, TaskProgress, TaskResponse, UIDResponse } from '@/types/project.types';
import { apiClient } from './client';

// Get config from runtime window object (loaded from config.js) — same source as client.ts
const appConfig = (window as any).__APP_CONFIG__ || {};
const SSE_BASE_URL = appConfig.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

/** GET /3d/project/{project_id}/model-upload-task */
export interface ModelUploadTaskResponse {
  task_id: string | null;
}

export const modelsApi = {
  // POST /3d/target/{project_id} or /3d/blank/{project_id}
  upload: async (
    projectId: string,
    file: File,
    modelType: ModelType,
    onUploadProgress?: (progress: number) => void
  ): Promise<UIDResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const endpoint = modelType === 'target' ? `/3d/target/${projectId}` : `/3d/blank/${projectId}`;

    // Extended timeout for file uploads: 15 minutes (900000 ms)
    // File upload and processing can take a long time
    const { data } = await apiClient.post<UIDResponse>(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 900000, // 15 minutes
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          if (progressEvent.total && progressEvent.total > 0) {
            const percentCompleted = Math.min(
              95, // Max 95% during upload, 100% only after completion
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            );
            onUploadProgress(percentCompleted);
          } else if (progressEvent.loaded) {
            // Fallback: if total is not available, use loaded bytes as indicator
            // This is a rough estimate
            const estimatedProgress = Math.min(95, Math.round((progressEvent.loaded / (file.size || 1)) * 100));
            onUploadProgress(estimatedProgress);
          }
        }
      },
    });

    return data;
  },

  // DELETE /3d/{model_id}
  delete: async (modelId: string): Promise<void> => {
    await apiClient.delete(`/3d/${modelId}`);
  },

  // GET /3d/{model_id}/download - download original STP file from S3
  downloadSTP: async (modelId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/3d/${modelId}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /preview/png/{model_id}
  getPngUrl: (modelId: string): string => {
    return `${apiClient.defaults.baseURL}/preview/png/${modelId}`;
  },

  // GET /preview/gif/{model_id}
  getGifUrl: (modelId: string): string => {
    return `${apiClient.defaults.baseURL}/preview/gif/${modelId}`;
  },

  // GET /preview/png/{model_id} - fetch as blob with auth
  fetchPngBlob: async (modelId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/preview/png/${modelId}`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /preview/gif/{model_id} - fetch as blob with auth
  fetchGifBlob: async (modelId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/preview/gif/${modelId}`, {
      responseType: 'blob',
    });
    return data;
  },

  // POST /3d/target/{project_id}/background or /3d/blank/{project_id}/background
  // Returns task ID for progress tracking
  uploadBackground: async (projectId: string, file: File, modelType: ModelType): Promise<TaskResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const endpoint =
      modelType === 'target' ? `/3d/target/${projectId}/background` : `/3d/blank/${projectId}/background`;

    const { data } = await apiClient.post<TaskResponse>(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 1 minute for initial upload, actual processing happens in background
    });

    return data;
  },

  // POST /3d/target_and_blank/{project_id}/background - upload both target and blank in one request
  // Returns single task ID; client tracks one SSE stream until complete
  uploadTargetAndBlankBackground: async (
    projectId: string,
    targetFile: File,
    blankFile: File
  ): Promise<UIDResponse> => {
    const formData = new FormData();
    formData.append('target_stp', targetFile);
    formData.append('blank_stp', blankFile);

    const { data } = await apiClient.post<UIDResponse>(`/3d/target_and_blank/${projectId}/background`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });

    return data;
  },

  // POST /3d/embedded/{project_id}/background - upload a single STP containing both
  // the target model and its stock; backend splits and stores them as TARGET + BLANK.
  uploadEmbeddedBackground: async (projectId: string, file: File): Promise<UIDResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<UIDResponse>(`/3d/embedded/${projectId}/background`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });

    return data;
  },

  // SSE progress tracking for background upload
  // Uses fetch() streaming so that Authorization header can be set (EventSource doesn't support custom headers)
  trackProgressSSE: async (
    taskId: string,
    onProgress: (progress: TaskProgress) => void,
    signal?: AbortSignal
  ): Promise<TaskProgress> => {
    // Retrieve token the same way as the Axios request interceptor in client.ts
    let token: string | undefined;
    try {
      const user = await userManager.getUser();
      token = user?.access_token;
    } catch (error) {
      console.warn('Failed to get user from userManager, trying storage fallback:', error);

      const authority = appConfig.VITE_AUTHORITY || import.meta.env.VITE_AUTHORITY;
      const clientId = appConfig.VITE_CLIENT_ID || import.meta.env.VITE_CLIENT_ID;
      const storageKey = `oidc.user:${authority}:${clientId}`;

      let oidcStorage = localStorage.getItem(storageKey);
      if (!oidcStorage) {
        oidcStorage = sessionStorage.getItem(storageKey);
      }

      if (oidcStorage) {
        try {
          const stored = JSON.parse(oidcStorage);
          token = stored?.access_token;
        } catch (parseError) {
          console.error('Failed to parse OIDC storage:', parseError);
        }
      }
    }

    const response = await fetch(`${SSE_BASE_URL}/3d/task/${taskId}/stream`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('SSE response has no body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastProgress: TaskProgress | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newlines
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;

          // Parse named SSE event block:
          // "event: <type>\ndata: <json>"
          let eventType = 'message';
          let dataLine = '';

          for (const line of block.split('\n')) {
            if (line.startsWith('event:')) {
              eventType = line.slice('event:'.length).trim();
            } else if (line.startsWith('data:')) {
              dataLine = line.slice('data:'.length).trim();
            }
          }

          if (!dataLine) continue;

          let parsed: any;
          try {
            parsed = JSON.parse(dataLine);
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError, dataLine);
            continue;
          }

          if (eventType === 'progress') {
            const progress: TaskProgress = {
              task_id: taskId,
              status: parsed.status,
              progress: parsed.progress ?? 0,
              current_step: parsed.current_step ?? '',
              created_at: parsed.created_at ?? new Date().toISOString(),
              updated_at: parsed.updated_at ?? new Date().toISOString(),
            };
            lastProgress = progress;
            onProgress(progress);
          } else if (eventType === 'completed') {
            const progress: TaskProgress = {
              task_id: taskId,
              status: parsed.status ?? 'completed',
              progress: parsed.progress ?? 100,
              current_step: parsed.current_step ?? '',
              created_at: parsed.created_at ?? new Date().toISOString(),
              updated_at: parsed.updated_at ?? new Date().toISOString(),
              result: parsed.result ?? undefined,
            };
            onProgress(progress);
            return progress;
          } else if (eventType === 'error') {
            throw new Error(parsed.error || 'Upload failed');
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // Stream ended without a completed/error event
    if (lastProgress) {
      throw new Error('SSE stream ended unexpectedly before task completion');
    }
    throw new Error('SSE stream ended without any progress data');
  },

  // Cancel a background task
  cancelTask: async (taskId: string): Promise<void> => {
    await apiClient.delete(`/3d/task/${taskId}`);
  },

  // HTTP polling fallback for progress tracking
  // Use this if WebSocket is not available
  getTaskStatus: async (taskId: string): Promise<TaskProgress> => {
    const { data } = await apiClient.get<TaskProgress>(`/3d/task/${taskId}/status`);
    return data;
  },

  /** Active background model upload task for this project (from server Redis registry). */
  getProjectModelUploadTask: async (projectId: string): Promise<ModelUploadTaskResponse> => {
    const { data } = await apiClient.get<ModelUploadTaskResponse>(`/3d/project/${projectId}/model-upload-task`);
    return data;
  },

  // Combined upload with progress tracking
  // This is the recommended way to upload with real-time progress
  uploadWithProgress: async (
    projectId: string,
    file: File,
    modelType: ModelType,
    onProgress: (progress: number, currentStep: string, taskId?: string) => void,
    onError?: (error: Error) => void,
    signal?: AbortSignal
  ): Promise<string> => {
    // Add timeout wrapper (20 minutes total timeout for large files)
    const UPLOAD_TIMEOUT = 20 * 60 * 1000; // 20 minutes

    return Promise.race([
      (async () => {
        try {
          // Start background upload
          const { uid: taskId } = await modelsApi.uploadBackground(projectId, file, modelType);

          // Track progress via SSE
          const result = await modelsApi.trackProgressSSE(
            taskId,
            (taskProgress) => {
              onProgress(taskProgress.progress, taskProgress.current_step, taskId);
            },
            signal
          );

          if (!result.result?.model_id) {
            throw new Error('Upload completed but no model ID received');
          }

          return result.result.model_id;
        } catch (error) {
          if (onError && error instanceof Error) {
            onError(error);
          }
          throw error;
        }
      })(),
      new Promise<string>((_, reject) => {
        setTimeout(() => {
          const timeoutError = new Error('Upload timeout - server is not responding');
          if (onError) {
            onError(timeoutError);
          }
          reject(timeoutError);
        }, UPLOAD_TIMEOUT);
      }),
    ]);
  },
};
